import { useMemo } from 'react'
import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { UserApiTokenRow } from '../UserApiTokenRow'
import { formatDate, formatDateTime } from '../../../../utils/date'
import { intl } from '../../../../i18n'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { colorPalette } from '../../../style/colorPalette'

const dateValueFormatter = (
  params: ValueFormatterParams<UserApiTokenRow, number>
): string => {
  if (!params.value) return ''
  return formatDate(params.value) ?? ''
}

const dateTimeValueFormatter = (
  params: ValueFormatterParams<UserApiTokenRow, number>
): string => {
  if (!params.value) return ''
  return formatDateTime(params.value) ?? ''
}

const scopesValueFormatter = (
  params: ValueFormatterParams<UserApiTokenRow, string[]>
): string => {
  if (!params.value || params.value.length === 0) return ''
  return params.value.join(', ')
}

const getStatusText = (data: UserApiTokenRow | undefined): string => {
  if (!data) return ''
  if (data.revoked) {
    return intl.formatMessage({ id: 'userApiToken.status.revoked' })
  }
  if (data.expired) {
    return intl.formatMessage({ id: 'userApiToken.status.expired' })
  }
  return intl.formatMessage({ id: 'userApiToken.status.active' })
}

const getStatusColor = (data: UserApiTokenRow | undefined): string => {
  if (!data) return ''
  if (data.revoked) return colorPalette.monotone[4]
  if (data.expired) return colorPalette.yellow[3]
  return colorPalette.green[5]
}

export const useColumnDefs = (): ColDef<UserApiTokenRow>[] => {
  const rowNumberColDef = useRowNumber()

  return useMemo(
    () => [
      {
        ...rowNumberColDef,
        pinned: 'left',
      } as ColDef<UserApiTokenRow>,
      {
        field: 'tokenName',
        headerName: intl.formatMessage({
          id: 'userApiToken.tokenName',
        }),
        minWidth: 150,
      },
      {
        field: 'scopes',
        headerName: intl.formatMessage({
          id: 'userApiToken.scopes',
        }),
        width: 250,
        minWidth: 150,
        valueFormatter: scopesValueFormatter,
      },
      {
        field: 'expiredAt',
        headerName: intl.formatMessage({
          id: 'userApiToken.expiredAt',
        }),
        width: 120,
        valueFormatter: dateValueFormatter,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'userApiToken.createdAt',
        }),
        width: 160,
        valueFormatter: dateTimeValueFormatter,
      },
      {
        field: 'lastUsedAt',
        headerName: intl.formatMessage({
          id: 'userApiToken.lastUsedAt',
        }),
        width: 160,
        valueFormatter: dateTimeValueFormatter,
      },
      {
        colId: 'status',
        headerName: intl.formatMessage({
          id: 'userApiToken.status',
        }),
        width: 120,
        valueGetter: params => getStatusText(params.data),
        cellStyle: params => ({
          color: getStatusColor(params.data),
          fontWeight: 'bold',
        }),
      },
    ],
    [rowNumberColDef]
  )
}
